import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';
import { useDescope, useSession, useUser } from '@descope/react-sdk';
import type { UserProfile } from './descope';
import { useIdleLogout } from './useIdleLogout';
import { parseAllowedModels } from './allowedModels';
import { parseInsightsEnabled } from './insightsEnabled';
import { parsePermissions, type PermissionName } from './permissions';
import { decodeJwtClaims, parseCanViewAs, parseViewingAs } from '@/features/viewAs/viewAs';
import type { AgentMode } from '@/features/chat/useChat';

export type { UserProfile };

interface AuthContextValue {
  user: UserProfile | null;
  /** The agents this user's tenant may use (Descope `allowedModels` claim). Always
   *  at least the default agent — see `parseAllowedModels`. */
  allowedModels: AgentMode[];
  /** Does this user's tenant get the Overview screen (Descope `insightsEnabled`
   *  claim)? Off unless switched on — see `parseInsightsEnabled`. */
  insightsEnabled: boolean;
  /** May this user open "View as customer" (Descope `Impersonate` permission)?
   *  Decides what renders; the server re-checks every /api/view-as/* call. */
  canViewAs: boolean;
  /** Does this user hold a tenant-facing RBAC permission (auth/permissions)?
   *  Decides what renders; the server re-checks the routes each one guards. */
  can: (permission: PermissionName) => boolean;
  /** Is this session viewing a customer right now (Descope `act` claim)? When
   *  true, `user` below is the CUSTOMER — that is the whole point — and the app
   *  is read-only. Enforced server-side; the UI only matches it. */
  viewingAs: boolean;
  /** True while Descope is resolving the session/user on load. */
  loading: boolean;
  /** No-op with Descope — the flow's onSuccess drives state. Kept for API compat. */
  signIn: () => void;
  signOut: () => void;
}

export type { AuthContextValue };

/** Exported for stories only. Storybook has no Descope session, so a story that
 *  needs a signed-in tenant (a claim like `insightsEnabled`) nests its own provider
 *  inside the real one — the nearest provider wins. The app never does this. */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Client-side auth gate backed by Descope. State flows one direction: the
 * `<Descope flowId="...">` component completes, Descope updates its own store,
 * `useSession`/`useUser` re-render this provider, and the UI switches.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isSessionLoading, claims, sessionToken } = useSession();
  const { user: dUser, isUserLoading } = useUser();
  const { logout } = useDescope();

  const user = useMemo<UserProfile | null>(() => {
    if (!isAuthenticated || !dUser) return null;
    return {
      email: dUser.email ?? '',
      name: dUser.name || dUser.email || 'User',
      picture: dUser.picture || undefined,
      sub: dUser.userId ?? '',
    };
  }, [isAuthenticated, dUser]);

  const signOut = useCallback(() => {
    // Drop the open chat from the URL. Signing out is "I'm done here" — leaving
    // /chat/<id> up means the next person to sign in on this browser lands on a chat
    // that isn't theirs and just gets an error. history, not navigate: this provider
    // sits ABOVE the Router (the gate isn't a route). A deep link opened while signed
    // out doesn't come through here, so it still keeps its URL and returns to it.
    window.history.replaceState(null, '', '/');
    void logout();
  }, [logout]);

  // Auto sign-out after 12h of inactivity (only while signed in). Deliberately NOT
  // `signOut`: it's the same user coming back, so keep the URL and return them to
  // the chat they left open.
  useIdleLogout(isAuthenticated, logout);

  const allowedModels = useMemo(() => parseAllowedModels(claims), [claims]);
  const insightsEnabled = useMemo(() => parseInsightsEnabled(claims), [claims]);
  // From the SESSION token, not from `claims` — see decodeJwtClaims for why the
  // SDK's claims value cannot answer these two.
  const sessionClaims = useMemo(() => decodeJwtClaims(sessionToken), [sessionToken]);
  const canViewAs = useMemo(() => parseCanViewAs(sessionClaims), [sessionClaims]);
  const permissions = useMemo(() => parsePermissions(sessionClaims), [sessionClaims]);
  const can = useCallback(
    (permission: PermissionName) => permissions.has(permission),
    [permissions],
  );
  // Latched for the life of this page load. `viewingAs` is derived from a token
  // that can be momentarily absent — cleared while expiring, a refresh in flight,
  // the SDK publishing its two signals a tick apart — and every read-only gate
  // plus the banner itself fail OPEN on that. Without the latch a blank instant
  // unmounts the banner and hands back every write affordance while the
  // customer's screens keep rendering, which is the one state this feature must
  // never produce. Safe to latch because BOTH directions of view-as navigate:
  // there is no legitimate in-page transition out of it.
  const everViewedAs = useRef(false);
  const viewingAsNow = useMemo(() => parseViewingAs(sessionClaims), [sessionClaims]);
  if (viewingAsNow) everViewedAs.current = true;
  const viewingAs = viewingAsNow || everViewedAs.current;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      allowedModels,
      insightsEnabled,
      canViewAs,
      can,
      viewingAs,
      loading: isSessionLoading || isUserLoading,
      signIn: () => {},
      signOut,
    }),
    [
      user,
      allowedModels,
      insightsEnabled,
      canViewAs,
      can,
      viewingAs,
      isSessionLoading,
      isUserLoading,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
